'use client';
import React, { startTransition } from 'react';
import { useEffectDidMount } from '@benzinga/hooks';
import {
  ArticleData,
  Campaigns,
  CampaignStrategy,
  getArticleCampaignQueryParams,
  getArticleCampaignStrategy,
  injectVendorInArticleUrls,
  setCampaignSessionReferrer,
} from '@benzinga/article-manager';

import { renderPrimisVideo } from '@benzinga/ads-utils';

interface CampaignData {
  campaign: Campaigns | null;
  campaignStrategy: CampaignStrategy | undefined;
  campaignTicker: string | undefined;
}

export const useCampaign = (
  articleData: ArticleData,
  strategy?: CampaignStrategy,
  wordCount?: number,
): CampaignData => {
  const [campaignStrategy, setCampaignStrategy] = React.useState<CampaignStrategy>(strategy ?? 'external');
  const [selectedCampaign, setSelectedCampaign] = React.useState<boolean>(false);

  const campaignTicker = (): string => {
    let primaryTicker = '';
    const primary = articleData.tickers?.find(ticker => ticker.primary);
    if (primary) {
      primaryTicker = primary.name;
    } else if (!primary && articleData.tickers?.length > 0) {
      primaryTicker = articleData.tickers[0].name;
    }
    return primaryTicker;
  };

  const articleCampaignStrategy = (): CampaignStrategy => {
    const query = getArticleCampaignQueryParams();

    if (strategy) {
      return strategy;
    }

    if (query.campaigns_strategy || query.utm_source || articleData?.metaProps) {
      return getArticleCampaignStrategy(query, articleData);
    }

    return wordCount && wordCount < 150 ? 'between' : 'bottom';
  };

  const renderInArticleVideo = (): void => {
    setTimeout(() => {
      renderPrimisVideo();
    });
  };

  useEffectDidMount(() => {
    if (!selectedCampaign) {
      setCampaignSessionReferrer();
      injectVendorInArticleUrls();
      const utm_source = articleCampaignStrategy();
      startTransition(() => {
        setCampaignStrategy(utm_source);
        setSelectedCampaign(true);

        if (['all', 'middle', 'middle-bottom'].includes(utm_source)) {
          renderInArticleVideo();
        }
      });
    }
  });

  return {
    campaign: null,
    campaignStrategy,
    campaignTicker: campaignTicker(),
  };
};
